import { type Accessor, type Setter, createSignal } from "solid-js";
import type { Block, Operation } from "./types";

// Granular stores for fine-grained reactivity
// Each operation and block gets its own signal for optimal performance

// Operation expansion state
const operationExpandedSignals = new Map<string, [Accessor<boolean>, Setter<boolean>]>();

// User interaction tracking for operations (whether user has manually interacted with any block)
const operationUserInteractionSignals = new Map<string, [Accessor<boolean>, Setter<boolean>]>();

// Block delta subscription system - pure event-based, no reactivity
type DeltaSubscriber = (delta: string) => void;
type BlockStopSubscriber = () => void;
type MetricsSubscriber = (metrics: BlockMetrics) => void;

const blockDeltaSubscribers = new Map<string, Set<DeltaSubscriber>>();
const blockStopSubscribers = new Map<string, Set<BlockStopSubscriber>>();
const blockMetricsSubscribers = new Map<string, Set<MetricsSubscriber>>();
const blockLastTextLength = new Map<string, number>();

// Content accumulator for blocks - allows components to catch up on existing content
const blockContentStore = new Map<string, string>();

// Block metrics for efficient updates
export interface BlockMetrics {
  ingressTokens: number;
  egressTokens: number;
  ingressBytes: number;
  egressBytes: number;
  ingressCredits?: number;
  egressCredits?: number;
}

// Event emitter for deltas - bypasses SolidJS reactivity entirely
export const emitBlockDelta = (blockId: string, delta: string): void => {
  // Store the content for late subscribers
  const currentContent = blockContentStore.get(blockId) || "";
  blockContentStore.set(blockId, currentContent + delta);

  // Emit to current subscribers
  const subscribers = blockDeltaSubscribers.get(blockId);
  if (subscribers && delta) {
    subscribers.forEach((subscriber) => {
      try {
        subscriber(delta);
      } catch (error) {
        console.warn("Error in delta subscriber:", error);
      }
    });
  }
};

export const emitBlockStop = (blockId: string): void => {
  const subscribers = blockStopSubscribers.get(blockId);
  if (subscribers) {
    subscribers.forEach((subscriber) => {
      try {
        subscriber();
      } catch (error) {
        console.warn("Error in block stop subscriber:", error);
      }
    });
  }
};

export const emitBlockMetrics = (blockId: string, metrics: BlockMetrics): void => {
  const subscribers = blockMetricsSubscribers.get(blockId);
  if (subscribers) {
    subscribers.forEach((subscriber) => {
      try {
        subscriber(metrics);
      } catch (error) {
        console.warn("Error in metrics subscriber:", error);
      }
    });
  }
};

// Block signals
const blockSignals = new Map<string, [Accessor<Block | undefined>, Setter<Block | undefined>]>();

// Operation signals
const operationSignals = new Map<string, [Accessor<Operation | undefined>, Setter<Operation | undefined>]>();

// Operation metadata (for titles, status, etc.)
export interface OperationMetadata {
  title: string;
  isProcessing: boolean;
  hasBlocks: boolean;
  maxDurationMs?: number;
  minDurationMs?: number;
  expectedDurationMs?: number;
  durationConfidence?: string;
  taskCategory?: string;
}

const operationMetadataSignals = new Map<string, [Accessor<OperationMetadata>, Setter<OperationMetadata>]>();

// === Operation Expansion ===

export const getOperationExpanded = (operationId: string): Accessor<boolean> => {
  if (!operationExpandedSignals.has(operationId)) {
    // Default to expanded for new operations
    operationExpandedSignals.set(operationId, createSignal(true));
  }
  return operationExpandedSignals.get(operationId)![0];
};

export const setOperationExpanded = (operationId: string, expanded: boolean): void => {
  if (!operationExpandedSignals.has(operationId)) {
    operationExpandedSignals.set(operationId, createSignal(expanded));
  } else {
    const [, setter] = operationExpandedSignals.get(operationId)!;
    setter(expanded);
  }
};

export const toggleOperationExpanded = (operationId: string): void => {
  const current = getOperationExpanded(operationId)();
  setOperationExpanded(operationId, !current);
};

// === User Interaction Tracking ===

export const getOperationUserInteracted = (operationId: string): Accessor<boolean> => {
  if (!operationUserInteractionSignals.has(operationId)) {
    // Default to false for new operations (no user interaction)
    operationUserInteractionSignals.set(operationId, createSignal(false));
  }
  return operationUserInteractionSignals.get(operationId)![0];
};

export const setOperationUserInteracted = (operationId: string, interacted: boolean): void => {
  if (!operationUserInteractionSignals.has(operationId)) {
    operationUserInteractionSignals.set(operationId, createSignal(interacted));
  } else {
    const [, setter] = operationUserInteractionSignals.get(operationId)!;
    setter(interacted);
  }
};

export const markOperationAsUserInteracted = (operationId: string): void => {
  setOperationUserInteracted(operationId, true);
};

// === Block Delta Subscription ===

// Subscribe to delta events - pure event system, no SolidJS reactivity
export const subscribeToBlockDeltas = (blockId: string, deltaSubscriber: DeltaSubscriber): (() => void) => {
  if (!blockDeltaSubscribers.has(blockId)) {
    blockDeltaSubscribers.set(blockId, new Set());
  }

  const subscribers = blockDeltaSubscribers.get(blockId)!;
  subscribers.add(deltaSubscriber);

  // Initialize text length tracking
  if (!blockLastTextLength.has(blockId)) {
    blockLastTextLength.set(blockId, 0);
  }

  // Send existing content to new subscriber (catch-up mechanism)
  const existingContent = blockContentStore.get(blockId);
  if (existingContent) {
    try {
      deltaSubscriber(existingContent);
    } catch (error) {
      console.warn("Error in delta subscriber during catch-up:", error);
    }
  }

  // Return unsubscribe function
  return () => {
    subscribers.delete(deltaSubscriber);
    if (subscribers.size === 0) {
      blockDeltaSubscribers.delete(blockId);
      blockLastTextLength.delete(blockId);
    }
  };
};

// Subscribe to block stop events - triggered when block completes
export const subscribeToBlockStop = (blockId: string, stopSubscriber: BlockStopSubscriber): (() => void) => {
  if (!blockStopSubscribers.has(blockId)) {
    blockStopSubscribers.set(blockId, new Set());
  }

  const subscribers = blockStopSubscribers.get(blockId)!;
  subscribers.add(stopSubscriber);

  // Return unsubscribe function
  return () => {
    subscribers.delete(stopSubscriber);
    if (subscribers.size === 0) {
      blockStopSubscribers.delete(blockId);
    }
  };
};

// Subscribe to block metrics events - triggered when metrics update
export const subscribeToBlockMetrics = (blockId: string, metricsSubscriber: MetricsSubscriber): (() => void) => {
  if (!blockMetricsSubscribers.has(blockId)) {
    blockMetricsSubscribers.set(blockId, new Set());
  }

  const subscribers = blockMetricsSubscribers.get(blockId)!;
  subscribers.add(metricsSubscriber);

  // Return unsubscribe function
  return () => {
    subscribers.delete(metricsSubscriber);
    if (subscribers.size === 0) {
      blockMetricsSubscribers.delete(blockId);
    }
  };
};

// === Block Content Access ===

// Get the current accumulated content for a block (for debugging/testing)
export const getBlockContent = (blockId: string): string => {
  return blockContentStore.get(blockId) || "";
};

// === Block Access ===

export const getBlock = (blockId: string): Accessor<Block | undefined> => {
  if (!blockSignals.has(blockId)) {
    blockSignals.set(blockId, createSignal<Block | undefined>(undefined));
  }
  return blockSignals.get(blockId)![0];
};

export const setBlock = (blockId: string, block: Block | undefined): void => {
  if (!blockSignals.has(blockId)) {
    blockSignals.set(blockId, createSignal(block));
  } else {
    const [, setter] = blockSignals.get(blockId)!;
    setter(block);
  }

  // Note: Delta emission is now handled directly in converter.ts via emitBlockDelta()
  // Block completion events are also handled in converter.ts via emitBlockStop()
};

// === Operation Access ===

export const getOperation = (operationId: string): Accessor<Operation | undefined> => {
  if (!operationSignals.has(operationId)) {
    operationSignals.set(operationId, createSignal<Operation | undefined>(undefined));
  }
  return operationSignals.get(operationId)![0];
};

export const setOperation = (operationId: string, operation: Operation | undefined): void => {
  if (!operationSignals.has(operationId)) {
    operationSignals.set(operationId, createSignal(operation));
  } else {
    const [, setter] = operationSignals.get(operationId)!;
    setter(operation);
  }
};

// === Operation Metadata ===

export const getOperationMetadata = (operationId: string): Accessor<OperationMetadata> => {
  if (!operationMetadataSignals.has(operationId)) {
    const defaultMetadata: OperationMetadata = {
      title: "Unknown Operation",
      isProcessing: false,
      hasBlocks: false,
    };
    operationMetadataSignals.set(operationId, createSignal(defaultMetadata));
  }
  return operationMetadataSignals.get(operationId)![0];
};

export const setOperationMetadata = (operationId: string, metadata: OperationMetadata): void => {
  if (!operationMetadataSignals.has(operationId)) {
    operationMetadataSignals.set(operationId, createSignal(metadata));
  } else {
    const [, setter] = operationMetadataSignals.get(operationId)!;
    setter(metadata);
  }
};

// === Batch Updates ===

export const updateBlocksAndOperations = (operations: Operation[]): void => {
  // Update all operations
  for (const operation of operations) {
    setOperation(operation.id, operation);

    // Update all blocks in this operation
    for (const block of operation.blocks) {
      setBlock(block.id, block);
    }
  }
};

// === Utilities ===

export const getAllOperationIds = (): string[] => {
  return Array.from(operationSignals.keys());
};

export const getAllBlockIds = (): string[] => {
  return Array.from(blockSignals.keys());
};

// Clean up unused signals (optional optimization)
export const cleanupUnusedSignals = (activeOperationIds: string[], activeBlockIds: string[]): void => {
  // Remove operation signals not in active list
  for (const operationId of operationSignals.keys()) {
    if (!activeOperationIds.includes(operationId)) {
      operationSignals.delete(operationId);
      operationExpandedSignals.delete(operationId);
      operationUserInteractionSignals.delete(operationId);
      operationMetadataSignals.delete(operationId);
    }
  }

  // Remove block signals not in active list
  for (const blockId of blockSignals.keys()) {
    if (!activeBlockIds.includes(blockId)) {
      blockSignals.delete(blockId);
      blockDeltaSubscribers.delete(blockId);
      blockStopSubscribers.delete(blockId);
      blockMetricsSubscribers.delete(blockId);
      blockLastTextLength.delete(blockId);
      blockContentStore.delete(blockId);
    }
  }
};
